import React, { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import CalendarDay from './CalendarDay';
import MonthLegendItem from './MonthLegendItem';
import {
    DAYS_OF_WEEK,
    CELL_SIZE,
    CELL_SPACING,
    NON_EMPTY_CELL_SCALE,
    getDaysInMonth,
    getMonthName,
    formatDate
} from '../../utils/dateUtils';

/**
 * Calendar month component that renders a grid of days
 *
 * @param {Object} props
 * @param {number} props.year - Year to display
 * @param {number} props.month - Month to display (0-11)
 * @param {Map} props.itemsByDate - Map of items by date string
 * @param {Array} props.batchRanges - Array of batch send date ranges for highlighting
 * @param {Function} props.onDayClick - Handler for clicking on a day
 * @param {boolean} props.isMoveMode - Whether in move mode
 * @param {Object} props.contentTypeColors - Map of content types to colors
 * @returns {JSX.Element}
 */

const CalendarMonth = ({ year, month, itemsByDate, batchRanges, onDayClick, isMoveMode, contentTypeColors }) => {
    const dayCells = useMemo(() => {
        const daysInMonth = getDaysInMonth(year, month);

        // Get the day of the week for the first day (0 = Sunday, 1 = Monday, etc.)
        let firstDayOfMonth = new Date(year, month, 1).getDay();

        // Adjust for Monday start (convert Sunday from 0 to 6)
        firstDayOfMonth = firstDayOfMonth === 0 ? 6 : firstDayOfMonth - 1;

        // Array for empty grid spaces before first day of month
        const emptyCells = Array(firstDayOfMonth).fill(null).map((_, index) => (
            <Box
                key={`empty-${index}`}
                sx={{
                    visibility: 'hidden',
                    width: CELL_SIZE,
                    height: CELL_SIZE
                }}
            />
        ));

        const days = Array(daysInMonth).fill(null).map((_, index) => {
            const day = index + 1;
            const date = new Date(year, month, day);
            const dateString = formatDate(date);

            // Get items for this day from our map for faster lookup
            const dayItems = itemsByDate.get(dateString) || [];

            return (
                <CalendarDay
                    key={dateString}
                    date={date}
                    items={dayItems}
                    batchRanges={batchRanges}
                    onDayClick={onDayClick}
                    isMoveMode={isMoveMode}
                    contentTypeColors={contentTypeColors}
                />
            );
        });

        // Fill the remaining cells of the last week with empty grid spaces
        const totalCells = emptyCells.length + days.length;
        const remainingCells = 7 - (totalCells % 7);
        const fillCells = remainingCells < 7 ? Array(remainingCells).fill(null).map((_, index) => (
            <Box
                key={`fill-${index}`}
                sx={{
                    visibility: 'hidden',
                    width: CELL_SIZE,
                    height: CELL_SIZE
                }}
            />
        )) : [];

        return [...emptyCells, ...days, ...fillCells];
    }, [year, month, itemsByDate, onDayClick, isMoveMode, contentTypeColors]);

    // Get items for this month to display in legend
    const monthItems = useMemo(() => {
        const monthStart = new Date(year, month, 1);
        const monthEnd = new Date(year, month + 1, 0);
        monthEnd.setHours(23, 59, 59, 999);

        const allMonthItems = [];

        for (let d = 1; d <= getDaysInMonth(year, month); d++) {
            const date = new Date(year, month, d);
            const dateString = formatDate(date);
            const itemsForDate = itemsByDate.get(dateString) || [];

            if (itemsForDate.length > 0) {
                allMonthItems.push(...itemsForDate);
            }
        }

        // Sort by scheduled date
        allMonthItems.sort((a, b) => new Date(a.scheduledFor) - new Date(b.scheduledFor));

        return allMonthItems;
    }, [year, month, itemsByDate, getDaysInMonth, formatDate]);

    // Calculate calendar grid width for consistent sizing
    // Add extra space to accommodate the scaled cells
    const extraSpace = 2 * NON_EMPTY_CELL_SCALE * parseInt(CELL_SIZE);
    const calendarWidth = `calc(7 * ${CELL_SIZE} + 6 * ${CELL_SPACING} + ${extraSpace}px)`;

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'left' }}>
            <Typography variant="h4" gutterBottom>
                {getMonthName(year, month)}
            </Typography>

            {/* Calendar container */}
            <Box sx={{
                width: calendarWidth,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center'
            }}>
                {/* Days of week header */}
                <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: `repeat(7, ${CELL_SIZE})`,
                    gap: CELL_SPACING,
                    width: '100%',
                    mb: CELL_SPACING
                }}>
                    {DAYS_OF_WEEK.map((day, i) => (
                        <Box key={i} sx={{
                            width: CELL_SIZE,
                            height: CELL_SIZE,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}>
                            <Typography variant="caption" fontWeight="bold">
                                {day}
                            </Typography>
                        </Box>
                    ))}
                </Box>

                {/* Calendar days */}
                <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: `repeat(7, ${CELL_SIZE})`,
                    gap: CELL_SPACING,
                    width: '100%',
                    padding: `${Math.floor(parseInt(CELL_SIZE) * (NON_EMPTY_CELL_SCALE - 1) / 2)}px`,
                    position: 'relative' 
                }}>
                    {dayCells}
                </Box>

                {/* Legend */}
                {monthItems.length > 0 && (
                    <Box sx={{
                        mt: 2,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1,
                        width: '100%'
                    }}>
                        {monthItems.map(item => (
                            <MonthLegendItem
                                key={item.id}
                                item={item}
                                date={new Date(item.scheduledFor)}
                                contentTypeColors={contentTypeColors}
                            />
                        ))}
                    </Box>
                )}
            </Box>
        </Box>
    );
};

export default React.memo(CalendarMonth);